import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';

import { PycnometerProfilesService } from 'src/app/services/pycnometer-profiles.service';
import {
  PycnometerProfile,
  PycnometerProfileSummary
} from '@models/pycnometer-profile.interface';

import { PycnometerProfileListComponent } from './pycnometer-profile-list/pycnometer-profile-list.component';
import {
  PycnometerProfileDetailComponent,
  ProfileSavedEvent
} from './pycnometer-profile-detail/pycnometer-profile-detail.component';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '@shared/components/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-pycnometer-profiles-page',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    PycnometerProfileListComponent,
    PycnometerProfileDetailComponent
  ],
  templateUrl: './pycnometer-profiles-page.component.html',
  styleUrl: './pycnometer-profiles-page.component.scss'
})
export class PycnometerProfilesPageComponent implements OnInit {
  private readonly profilesService = inject(PycnometerProfilesService);
  private readonly dialog = inject(MatDialog);

  protected profiles = signal<PycnometerProfileSummary[]>([]);
  protected selectedProfile = signal<PycnometerProfile | null>(null);
  protected selectedId = signal<number | null>(null);
  protected isLoadingList = signal(false);
  protected isLoadingDetail = signal(false);

  ngOnInit(): void {
    this.loadList();
  }

  private loadList(selectId?: number, forceRefresh = false): void {
    this.isLoadingList.set(true);
    this.profilesService.getList(forceRefresh).subscribe({
      next: list => {
        this.profiles.set(list);
        this.isLoadingList.set(false);

        // Re-select after refresh, or restore previous selection
        const targetId = selectId ?? this.selectedId();
        if (targetId !== null) {
          const stillPresent = list.find(p => p.id === targetId);
          if (stillPresent) {
            this.loadDetail(targetId, forceRefresh && selectId === targetId);
          } else {
            this.selectedProfile.set(null);
            this.selectedId.set(null);
          }
        }
      },
      error: () => {
        this.isLoadingList.set(false);
      }
    });
  }

  private loadDetail(id: number, forceRefresh = false): void {
    this.isLoadingDetail.set(true);
    this.selectedId.set(id);
    this.profilesService.getById(id, forceRefresh).subscribe({
      next: profile => {
        this.selectedProfile.set(profile);
        this.isLoadingDetail.set(false);
      },
      error: () => {
        this.selectedProfile.set(null);
        this.isLoadingDetail.set(false);
      }
    });
  }

  protected onProfileSelected(summary: PycnometerProfileSummary): void {
    this.loadDetail(summary.id);
  }

  protected onAddClicked(): void {
    // Create an unsaved "new" profile shell
    const newProfile: PycnometerProfile = {
      id: 0,
      name: null,
      referenceVolume: 0,
      cellVolume: 0,
      createdOn: new Date().toISOString()
    };
    this.selectedProfile.set(newProfile);
    this.selectedId.set(null);
  }

  protected onProfileSaved(event: ProfileSavedEvent): void {
    // Reload the list and re-select the saved profile
    this.selectedId.set(event.id);
    this.loadList(event.id, true);
  }

  protected onProfileDeleted(profileId: number): void {
    const dialogRef = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData, boolean>(
      ConfirmationDialogComponent,
      {
        data: {
          title: 'Delete Profile',
          primaryText: 'Are you sure you want to delete this profile?',
          confirmText: 'Delete',
        },
        width: '420px',
        disableClose: true,
      }
    );
    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.profilesService.delete(profileId).subscribe({
        next: () => {
          // Clear selection if the deleted profile was selected
          if (this.selectedId() === profileId) {
            this.selectedProfile.set(null);
            this.selectedId.set(null);
          }
          // Reload the list
          this.loadList();
        },
        error: () => {
          alert('Failed to delete profile. Please try again.');
        }
      });
    });
  }
}
